import React, { useCallback, useEffect, useState } from "react";
import InquiryList from "./InquiryList";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { Container, Toast, ToastContainer } from "react-bootstrap";
import { useLoader } from "../../context/LoaderContext";
import DashboardCard from "../../components/dashboard-card";
import { getInquiryList } from "../../redux/slice/inquirySlice";
import "../../assets/styles/src/container/pages/npa-properties.css";
import TotalInquiryIcon from "../../assets/images/npa-properties/total-inquiry-icon.png";
import InquiryView from "./InquiryView";
import CommonPagination from "../../components/commonPagination";
import { CheckmarkBadge04Icon } from "../../assets/icons";

const Inquiry = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("toast-success");
  // Toast
  const [searchTerm,setSearchTerm] = useState("");
  // Open Inquiry Modal
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [selectedInquiryItem, setSelectedInquiryItem] = useState("");
  // Pagination
  const [
    limit,
    // setLimit
  ] = useState({ value: 10, label: 10 });
  const [currentPage, setCurrentPage] = useState(1);

  const { data, total } = useSelector((state) => state?.inquiry?.inquiryList);
  // console.log("currentPage",currentPage);

  // Pagination Handler
  const handlePageChange = (newPageNumber) => {
    setCurrentPage(newPageNumber);
  };

  // Handler for Opening View Modal
  const handleOpenViewModal = (item) => {
    setSelectedInquiryItem(item);
    setOpenInquiryModal(true);
  };

  // Handler for Closing View Modal
  const handleCloseViewModal = () => {
    setOpenInquiryModal(false);
  };

   // Api Call for Search
   useEffect(() => {
    const delayTimeout = setTimeout(() => {
      dispatch(getInquiryList({ search: searchTerm,page: currentPage }));
    }, 2000); // 3 second delay

    // Cleanup function to clear the timeout if searchTerm changes before the delay
    return () => clearTimeout(delayTimeout);
  }, [searchTerm,currentPage, dispatch]);

  // Api Call for fetching an Inquiry List
  const getAllInquiryList = useCallback(() => {
    setLoading(true);
    dispatch(getInquiryList({ page: currentPage, limit: limit?.value}))
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [dispatch, limit?.value, currentPage, setLoading]);

  useEffect(() => {
    getAllInquiryList();
  }, [getAllInquiryList]);

    // Handler for Export Inquiry Array
  const handleExportInquiry = () => {
    try{
      setLoading(true);
      dispatch(getInquiryList({ search: searchTerm, limit: 100000 })).then((res) => {
        const data = res?.payload?.data
        
        if (!data || data.length === 0){
          setSnackbarMessage("No data available to Export");
          setSnackbarVariant("toast-success");
          setShowSnackbar(true);
          setLoading(false);
          return;
        }

        // Map the data for export
        const dataToExport = data?.map((item) => {
          return {
            property_id: item?.property?.property_number_id || "-",
            name: item?.user_name || "-",
            mobile_number: item?.phone_number || "-",
            email: item?.email || "-",
            property_type: item?.property?.propertyType?.name || "-",
            possession_type: item?.property?.possessionType?.name || "-",
            message: item?.message || "-",
            inquiry_created_at: item?.created_at ? new Date(item.created_at).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }) : "-",
          }
        })

        // Export to Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "NPA Inquiries");
        const fileName = "NPA Inquiries.xlsx";
        XLSX.writeFile(workbook, fileName);
        setLoading(false);
        dispatch(getInquiryList({ search: searchTerm, limit: 10 }))
      })
    }catch(error){
      console.error("Error exporting data: ", error);
      setSnackbarMessage("Error exporting data: ", error);
      setSnackbarVariant("toast-success");
      setShowSnackbar(true);
      setLoading(false); // Disable loading state on error
    }  
  };

  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          onClose={() => setShowSnackbar(false)}
          show={showSnackbar}
          delay={5000}
          autohide
          bg={snackbarVariant}
          className="position-fixed top-3 end-0 me-3 shadow-none"
        >
          <Toast.Body className="d-flex align-items-center gap-1 lh-sm">
            <CheckmarkBadge04Icon
              width={18}
              height={18}
              color="#28c76f"
              strokeWidth="2"
            />
            <span className="text-capitalize">{snackbarMessage}</span>
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <Container fluid className="px-0">
        {/* Card Section */}
        <div className="grid gap-4">
          <DashboardCard
            totalUser={total}
            title="Total Inquiry"
            classNameMain="g-col-4 g-col-xxl-3"
            src={TotalInquiryIcon}
            alt="TotalInquiryIcon"
          />
        </div>

        {/* List Section */}
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-5">
          <InquiryList
            data={data}
            searchTerm={searchTerm}
            setCurrentPage={setCurrentPage}
            setSearchTerm={setSearchTerm}
            handleOpenViewModal={handleOpenViewModal}
            handleExportInquiry={handleExportInquiry}
          />

          {/* Pagination */}
          <div className="p-3 mx-1 d-flex flex-wrap align-items-center justify-content-between gap-4">
              <p className="custom-table-infotext ">
              Displaying {Math.min((currentPage - 1) * limit?.value + 1, total)}{" "}
              to {Math.min(currentPage * limit?.value, total)} of {total}
            </p>
            <div className="d-flex gap-4">
              {total > limit?.value && (
                <>
                  {/* <CreatableSelect
                    isClearable
                    options={recordsPerPageOptions}
                    value={limit}
                    onChange={handleChange}
                    placeholder="Select or create"
                    menuPortalTarget={document.body} // Render the menu at the body level
                    menuPlacement="top" // Display the options above the input
                    styles={{
                      control: (base) => ({
                        ...base,
                        width: "170px", // Fixed width for the input field
                      }),
                      menu: (base) => ({
                        ...base,
                        width: "170px", // Fixed width for the dropdown menu
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999, // Ensure it appears above everything
                      }),
                    }}
                  /> */}

                  <CommonPagination
                    totalItems={total}
                    itemsPerPage={limit?.value}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <InquiryView
          show={openInquiryModal}
          handleClose={handleCloseViewModal}
          selectedInquiryItem={selectedInquiryItem}
        />
      </Container>
    </>
  );
};

export default Inquiry;
